import type { MeetingDetailDTO, MeetingSummaryDTO, Outcome } from '@copilot/shared';
import { OutcomeSchema } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { meetingsApi } from '../../api/meetings';
import { useUser } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  EmptyState,
  ErrorBanner,
  PageSkeleton,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../../components';
import { formatDate, formatDateTime, formatDuration, formatPercent, talkRatioMe } from '../../lib/format';
import { OUTCOME_LABELS, PLATFORM_LABELS } from '../../lib/labels';
import { MeetingStatusBadge, OutcomeBadge, SentimentBadge } from './badges';
import { TranscriptViewer } from './TranscriptViewer';

export function MeetingDetailPage() {
  const { id = '' } = useParams();
  const user = useUser();
  const query = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    // Poll while the post-call summary is being generated.
    refetchInterval: (q) => (q.state.data?.summaryStatus === 'pending' ? 5000 : false),
  });
  const backTo = user.role === 'rep' ? '/my-meetings' : '/meetings';

  if (query.isLoading) return <PageSkeleton />;
  if (query.isError)
    return (
      <div className="space-y-3">
        <BackLink to={backTo} />
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  const m = query.data;
  if (!m) return null;

  return (
    <div className="space-y-4">
      <BackLink to={backTo} />
      <Header m={m} backTo={backTo} />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
        <div className="min-w-0 space-y-4">
          <SummarySection m={m} />
        </div>
        <div className="space-y-4">
          {m.summary && <SentimentCard sentiment={m.summary.sentiment} />}
          <TalkTimeCard m={m} />
          <DetailsCard m={m} />
          <ContactsCard m={m} />
          {m.preCallNotes && (
            <Card title="Pre-call notes">
              <p className="text-sm whitespace-pre-line text-ink-2">{m.preCallNotes}</p>
            </Card>
          )}
        </div>
      </div>
      <TranscriptViewer meetingId={m.id} tips={m.tips} available={m.transcriptAvailable} />
    </div>
  );
}

function BackLink({ to }: { to: string }) {
  return (
    <Link to={to} className="text-xs text-muted hover:text-ink">
      ← Back to meetings
    </Link>
  );
}

function Header({ m, backTo }: { m: MeetingDetailDTO; backTo: string }) {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const exportMd = useMutation({ mutationFn: () => meetingsApi.exportMarkdown(m.id), onError: setActionError });
  const exportPdf = useMutation({
    mutationFn: () => meetingsApi.recordPdfExport(m.id),
    onSuccess: () => void navigate(`/meetings/${m.id}/print`),
    onError: setActionError,
  });
  const remove = useMutation({
    mutationFn: () => meetingsApi.remove(m.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.removeQueries({ queryKey: ['meeting', m.id] });
      void navigate(backTo, { replace: true });
    },
  });

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink">{m.title || 'Untitled meeting'}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted">
            <span>{formatDateTime(m.startedAt)}</span>
            <span>·</span>
            <span>{m.repName}</span>
            {m.clientName && (
              <>
                <span>·</span>
                <span>{m.clientName}</span>
              </>
            )}
            <MeetingStatusBadge status={m.status} />
            <OutcomeBadge outcome={m.outcome} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => exportMd.mutate()} loading={exportMd.isPending}>
            Export Markdown
          </Button>
          <Button onClick={() => exportPdf.mutate()} loading={exportPdf.isPending}>
            Export PDF
          </Button>
          {user.role === 'admin' && (
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>
      <ErrorBanner error={actionError} className="mt-3" />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete meeting"
        danger
        requireText="DELETE"
        confirmLabel="Delete meeting"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutate()}
        onCancel={() => {
          setConfirmDelete(false);
          remove.reset();
        }}
      >
        This permanently deletes the meeting, its transcript, summary and tips. This action is recorded in the audit log and
        cannot be undone.
      </ConfirmDialog>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-2 py-1 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  );
}

function DetailsCard({ m }: { m: MeetingDetailDTO }) {
  const user = useUser();
  const queryClient = useQueryClient();
  const setOutcome = useMutation({
    mutationFn: (outcome: Outcome | null) => meetingsApi.patch(m.id, { outcome }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meeting', m.id] });
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
  return (
    <Card title="Details">
      <dl>
        <DetailRow label="Rep">
          {m.repName}
          {m.teamName && <span className="text-muted"> · {m.teamName}</span>}
        </DetailRow>
        <DetailRow label="Client">
          {m.clientId && m.clientName ? (
            user.role === 'rep' ? (
              m.clientName
            ) : (
              <Link to={`/clients/${m.clientId}`} className="text-accent-ink hover:underline">
                {m.clientName}
              </Link>
            )
          ) : (
            <span className="text-muted">Not linked</span>
          )}
        </DetailRow>
        <DetailRow label="Platform">{PLATFORM_LABELS[m.platform]}</DetailRow>
        <DetailRow label="Started">{formatDateTime(m.startedAt)}</DetailRow>
        <DetailRow label="Ended">{formatDateTime(m.endedAt)}</DetailRow>
        <DetailRow label="Duration">{formatDuration(m.durationSec)}</DetailRow>
        <DetailRow label="Status">
          <MeetingStatusBadge status={m.status} />
        </DetailRow>
        <DetailRow label="Outcome">
          {user.role === 'admin' ? (
            <div className="flex items-center gap-2">
              <Select
                aria-label="Outcome"
                value={m.outcome ?? ''}
                disabled={setOutcome.isPending}
                onChange={(e) => setOutcome.mutate((e.target.value || null) as Outcome | null)}
              >
                <option value="">Not set</option>
                {OutcomeSchema.options.map((o) => (
                  <option key={o} value={o}>
                    {OUTCOME_LABELS[o]}
                  </option>
                ))}
              </Select>
              {setOutcome.isPending && <Spinner size="sm" />}
            </div>
          ) : (
            <OutcomeBadge outcome={m.outcome} />
          )}
          {m.summary?.suggestedOutcome && m.summary.suggestedOutcome !== m.outcome && (
            <div className="mt-1 text-xs text-muted">AI suggested: {OUTCOME_LABELS[m.summary.suggestedOutcome]}</div>
          )}
          <ErrorBanner error={setOutcome.error} className="mt-1" />
        </DetailRow>
        <DetailRow label="Tags">
          {m.tags.length ? (
            <span className="flex flex-wrap gap-1">
              {m.tags.map((t) => (
                <Badge key={t}>{t}</Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted">None</span>
          )}
        </DetailRow>
        <DetailRow label="Consent">
          {m.consentConfirmedAt ? `Confirmed ${formatDateTime(m.consentConfirmedAt)}` : <span className="text-muted">Not recorded</span>}
        </DetailRow>
      </dl>
    </Card>
  );
}

function TalkTimeCard({ m }: { m: MeetingDetailDTO }) {
  const ratio = talkRatioMe(m.talkTime);
  if (!m.talkTime || ratio == null) return null;
  return (
    <Card title="Talk time">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ink">
          Rep {formatPercent(ratio)} <span className="text-muted">· {formatDuration(m.talkTime.meSec)}</span>
        </span>
        <span className="text-muted">
          Client {formatPercent(1 - ratio)} · {formatDuration(m.talkTime.themSec)}
        </span>
      </div>
      <div className="mt-2 flex h-2 gap-0.5 overflow-hidden rounded-full">
        <span className="grow-x bg-series-1" style={{ width: `${ratio * 100}%` }} />
        <span className="flex-1 bg-series-track" />
      </div>
    </Card>
  );
}

function ContactsCard({ m }: { m: MeetingDetailDTO }) {
  return (
    <Card title="Contacts">
      {m.contacts.length === 0 ? (
        <p className="text-sm text-muted">No contacts linked to this meeting.</p>
      ) : (
        <ul className="space-y-2">
          {m.contacts.map((c) => (
            <li key={c.id} className="text-sm">
              <div className="text-ink">{c.name || c.email || 'Unnamed contact'}</div>
              <div className="text-xs text-muted">{[c.title, c.email].filter(Boolean).join(' · ')}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function BulletList({ items, empty }: { items: string[]; empty: string }) {
  if (items.length === 0) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-ink">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  );
}

const SENTIMENT_ACCENT = {
  very_positive: 'good',
  positive: 'good',
  neutral: 'neutral',
  negative: 'danger',
  very_negative: 'danger',
  unknown: 'neutral',
} as const;

function SentimentCard({ sentiment }: { sentiment: MeetingSummaryDTO['sentiment'] }) {
  if (!sentiment) {
    return (
      <Card title="Client sentiment">
        <p className="text-sm text-muted">Not analysed yet. Regenerate the summary to add it.</p>
      </Card>
    );
  }
  return (
    <Card title="Client sentiment" accent={SENTIMENT_ACCENT[sentiment.level]} actions={<SentimentBadge level={sentiment.level} />}>
      {sentiment.reason && <p className="text-sm text-ink">{sentiment.reason}</p>}
      {sentiment.signals.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-2">
          {sentiment.signals.map((signal, i) => (
            <li key={i}>{signal}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SummarySection({ m }: { m: MeetingDetailDTO }) {
  if (m.summaryStatus === 'pending') {
    return (
      <Card title="Summary">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size="sm" /> The summary is being generated. This page refreshes automatically.
        </div>
      </Card>
    );
  }
  if (m.summaryStatus === 'failed' && !m.summary) {
    return (
      <Card title="Summary">
        <ErrorBanner error={new Error('Summary generation failed. The rep or an admin can regenerate it from the desktop app.')} />
      </Card>
    );
  }
  const s = m.summary;
  if (!s) {
    return (
      <Card title="Summary">
        <EmptyState title="No summary">
          {m.status === 'live' ? 'The call is still in progress.' : 'No summary was generated for this meeting.'}
        </EmptyState>
      </Card>
    );
  }

  const email = `Subject: ${s.followUpEmail.subject}\n\n${s.followUpEmail.body}`;
  return (
    <>
      <Card
        title="Summary"
        actions={
          <>
            {s.editedByRep && <Badge tone="accent">Edited by rep</Badge>}
            <span className="text-xs text-muted">Updated {formatDateTime(s.updatedAt)}</span>
          </>
        }
      >
        <BulletList items={s.summary} empty="No summary bullets." />
      </Card>

      <Card title="Next steps" bodyClassName="p-0">
        {s.nextSteps.length === 0 ? (
          <p className="p-4 text-sm text-muted">No next steps.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Step</Th>
                <Th>Owner</Th>
                <Th>Due</Th>
              </tr>
            </thead>
            <tbody>
              {s.nextSteps.map((n, i) => (
                <tr key={i}>
                  <Td className={n.done ? 'text-muted line-through' : undefined}>{n.text}</Td>
                  <Td className="whitespace-nowrap">{n.owner || <span className="text-muted">—</span>}</Td>
                  <Td className="whitespace-nowrap">{n.dueDate ? formatDate(n.dueDate) : <span className="text-muted">—</span>}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card title="Pain points">
          <BulletList items={s.painPoints} empty="None captured." />
        </Card>
        <Card title="Requirements">
          <BulletList items={s.requirements} empty="None captured." />
        </Card>
        <Card title="Objections">
          <BulletList items={s.objections} empty="None raised." />
        </Card>
      </div>

      <Card title="Follow-up email" actions={<CopyButton text={email} label="Copy email" />}>
        <div className="text-sm">
          <div className="mb-2">
            <span className="text-muted">Subject: </span>
            <span className="font-medium text-ink">{s.followUpEmail.subject}</span>
          </div>
          <pre className="font-sans whitespace-pre-wrap text-ink">{s.followUpEmail.body}</pre>
        </div>
      </Card>
    </>
  );
}
